import React, { useCallback, useState } from 'react';
import { useMutation } from 'react-query';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { EventType } from '~/Models/DTO/Event.model';
import { REQUEST_ACTIONS, STATUS } from '~/Constants/Actions.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { ToastVariant, useToastMessage } from '~/Hooks';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { EventGroup } from '~/Models/DTO/EventGroup.model';

export const useDeleteEventGroup = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const dispatch = useDispatch();
  const { showToast } = useToastMessage();
  const [deleteEventGroupOptions, setDeleteEventGroupOptions] = useState<EventGroup | void>();

  const deleteEventGroup = useMutation(
    async ({ type, id, ...eventGroup }: EventGroup) => {
      const url = routeReplacer(
        type === EventType.FREE_TRAINING
          ? EVENT_SERVICE_URLS.SINGLE_FREE_TRAININGS
          : EVENT_SERVICE_URLS.SINGLE_EVENTGROUP,
        { id },
      );

      // TODO: remove archiving when BE make deletion without archiving possible
      await requestReactQuery<EventGroup>({
        url,
        data: { ...eventGroup, type, archived: true },
        config: { method: 'PUT' },
      });

      await requestReactQuery<EventGroup>({
        url,
        config: { method: 'DELETE' },
      });

      dispatch({
        type: REQUEST_ACTIONS.DELETE_EVENTGROUP,
        name: REQUEST_ACTIONS.DELETE_EVENTGROUP,
        status: STATUS.SUCCESS,
        payload: { id },
      });
    },
    {
      onSuccess,
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'Event update error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
    },
  );
  const onCloseDeleteEventGroupModal = useCallback(() => setDeleteEventGroupOptions(), [
    setDeleteEventGroupOptions,
  ]);
  const onConfirmDeleteEventGroup = () => {
    if (deleteEventGroupOptions) {
      deleteEventGroup.mutate(deleteEventGroupOptions);
    }
    onCloseDeleteEventGroupModal();
  };

  return {
    deleteEventGroupOptions,
    onConfirmDeleteEventGroup,
    onOpenDeleteEventGroupModal: setDeleteEventGroupOptions,
    onCloseDeleteEventGroupModal,
  };
};
